import { useApolloClient } from '@apollo/client';
import { useRef, useState } from 'react';
import { User, useUserContext } from '../../contexts';
import {
  DataModelV2UpgradeJobFieldsFragment,
  FetchUserQuery,
  HasUnlockedSpecIdV1DataQuery,
  EnumDataModelV2UpgradeState as State,
} from '../../graphql/types';
import { getApolloErrorMessages } from '../../lib/errors';
import { useQueryAndRefetchOnUserChange } from '../../lib/hooks';
import { USER_QUERY } from '../../providers/UserProvider/queries';
import { showErrorFlash } from '../Flash';
import confirmSpecIdMigration from './confirmSpecIdMigration';
import {
  HAS_UNLOCKED_SPEC_ID_V1_DATA,
  useAcknowledgeV2Upgrade,
  useLatestDataModelV2UpgradeJob,
  useRunDataModelV2UpgradeJob,
} from './queries';
import { resolveUpgradeStatus } from './resolveUpgradeStatus';
import usePromptDismissal from './usePromptDismissal';

function latestJob(
  previous: DataModelV2UpgradeJobFieldsFragment | undefined,
  next: DataModelV2UpgradeJobFieldsFragment,
) {
  if (!previous) return next;
  const nextTime = new Date(next.updatedAt).getTime();
  const previousTime = new Date(previous.updatedAt).getTime();
  return !Number.isFinite(previousTime) || nextTime >= previousTime ? next : previous;
}

export default function useUpgradeFlow(user: NonNullable<User>) {
  const client = useApolloClient();
  const { isTokenTaxAdmin } = useUserContext();
  const { dismissed, dismiss } = usePromptDismissal(user.id);
  const [manuallyOpened, setManuallyOpened] = useState(false);
  const [introductionComplete, setIntroductionComplete] = useState(false);
  const [closedStatus, setClosedStatus] = useState<string | null>(null);
  const [upgradeRequested, setUpgradeRequested] = useState(false);
  const [knownJob, setKnownJob] = useState<DataModelV2UpgradeJobFieldsFragment>();
  const [acknowledged, setAcknowledged] = useState(false);
  const actionInFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const [runUpgrade] = useRunDataModelV2UpgradeJob();
  const [acknowledge] = useAcknowledgeV2Upgrade();

  const upgradeState = user.dataModelV2UpgradeState;
  const starting =
    upgradeRequested &&
    upgradeState !== State.upgrade_started &&
    upgradeState !== State.upgrade_completed &&
    upgradeState !== State.upgrade_acknowledged;
  const running = upgradeState === State.upgrade_started || starting;
  const canOpen =
    (isTokenTaxAdmin || upgradeState != null) &&
    upgradeState !== State.upgrade_acknowledged &&
    (user.usesGroupsSince == null || upgradeState === State.upgrade_started);
  const needsJob = running || upgradeState === State.upgrade_completed;

  const jobQuery = useLatestDataModelV2UpgradeJob({
    skip: !needsJob,
    pollInterval: running ? 3000 : 0,
    onCompleted: ({ latestDataModelV2UpgradeJob }) => {
      if (latestDataModelV2UpgradeJob) {
        setKnownJob((previous) => latestJob(previous, latestDataModelV2UpgradeJob));
      }
    },
    onError: () => showErrorFlash('Unable to check your upgrade status. Please try again.'),
  });

  useQueryAndRefetchOnUserChange<FetchUserQuery>(USER_QUERY, {
    skip: !running,
    fetchPolicy: 'network-only',
    pollInterval: running ? 2000 : 0,
  });

  const receivedJob = (jobQuery.data ?? jobQuery.previousData)?.latestDataModelV2UpgradeJob;
  const job = receivedJob ? latestJob(knownJob, receivedJob) : knownJob;
  const resolvedStatus = resolveUpgradeStatus({
    upgradeState: starting ? State.upgrade_started : upgradeState,
    usesGroupsSince: user.usesGroupsSince,
    promptDismissed: dismissed,
    manuallyOpened,
    jobLoaded: Boolean(job),
    jobStatus: job?.status,
  });
  const displayStatus = resolvedStatus === 'ready' && !introductionComplete ? 'intro' : resolvedStatus;
  const status = acknowledged || closedStatus === displayStatus ? null : displayStatus;

  const open = () => {
    if (!canOpen) return;
    setClosedStatus(null);
    setManuallyOpened(true);
    setIntroductionComplete(false);
    if (needsJob) void jobQuery.refetch().catch(() => {});
  };

  // `notNow` guards on `actionInFlight`, which `start` holds, so the dismissal body lives here for
  // both to share.
  const dismissPrompt = () => {
    dismiss();
    setClosedStatus(status);
    setManuallyOpened(false);
  };

  const start = async () => {
    if (actionInFlight.current) return;
    actionInFlight.current = true;
    setPending(true);
    try {
      // Fails closed: a failed check falls into the catch below rather than silently upgrading
      // someone whose Spec ID data needs to be migrated by hand.
      const specId = await client.query<HasUnlockedSpecIdV1DataQuery>({
        query: HAS_UNLOCKED_SPEC_ID_V1_DATA,
        fetchPolicy: 'network-only',
      });
      if (specId.data.hasUnlockedSpecIdV1Data && !(await confirmSpecIdMigration())) {
        dismissPrompt();
        return;
      }

      const result = await runUpgrade();
      if (!result.data) throw new Error('No upgrade job was returned.');
      setKnownJob(result.data.runDataModelV2UpgradeJob);
      setUpgradeRequested(true);
      setClosedStatus(null);
      // Polling refreshes the job; a failed refresh must not undo a successful start.
      void client.query({ query: USER_QUERY, fetchPolicy: 'network-only' }).catch(() => {
        showErrorFlash('Your upgrade has started, but we could not refresh its status. We’ll keep checking.');
      });
    } catch (error) {
      // e.g. the API refuses while the account has syncs or recalculations in progress, and says which
      showErrorFlash(getApolloErrorMessages(error) ?? 'Unable to start your upgrade. Please try again.');
    } finally {
      setPending(false);
      actionInFlight.current = false;
    }
  };

  const close = async (): Promise<boolean> => {
    if (actionInFlight.current) return false;
    // admins must not acknowledge the account owner's success message on their behalf.
    if (status === 'success' && !isTokenTaxAdmin) {
      actionInFlight.current = true;
      setPending(true);
      try {
        const result = await acknowledge();
        if (result.data?.acknowledgeV2Upgrade !== true) throw new Error('Acknowledgment was not saved.');
        setAcknowledged(true);
        void client.query({ query: USER_QUERY, fetchPolicy: 'network-only' }).catch(() => {});
      } catch {
        showErrorFlash('Unable to confirm your upgrade acknowledgment. Please try again.');
        return false;
      } finally {
        setPending(false);
        actionInFlight.current = false;
      }
    } else {
      setClosedStatus(status);
    }
    setManuallyOpened(false);
    return true;
  };

  const notNow = () => {
    if (actionInFlight.current || status !== 'ready') return;
    dismissPrompt();
  };

  const continueToConfirmation = () => {
    if (status === 'intro') setIntroductionComplete(true);
  };

  return { status, job, canOpen, open, start, close, notNow, pending, continueToConfirmation };
}
